import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  FormControlLabel,
  Snackbar,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { ServerRoutes } from "../routes";
import axios from "../utils/axios-instance";

interface LiveIndicatorSettings {
  isLive: boolean;
  nextLiveAt: string | null;
  liveUrl: string | null;
}

const isValidLiveUrl = (value: string): boolean => {
  if (!value.trim()) return true;

  try {
    const url = new URL(value.trim());
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

export function LiveStreamSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLive, setIsLive] = useState(false);
  const [nextLiveAt, setNextLiveAt] = useState<Dayjs | null>(null);
  const [liveUrl, setLiveUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;

    axios
      .get<LiveIndicatorSettings>(ServerRoutes.liveIndicator)
      .then(({ data }) => {
        if (cancelled) return;
        setIsLive(data.isLive);
        setNextLiveAt(data.nextLiveAt ? dayjs(data.nextLiveAt) : null);
        setLiveUrl(data.liveUrl ?? "");
      })
      .catch(() => {
        if (!cancelled) setError("Could not load the live settings.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const { data } = await axios.patch<LiveIndicatorSettings>(
        ServerRoutes.liveIndicator,
        {
          isLive,
          nextLiveAt: nextLiveAt?.toISOString() ?? null,
          liveUrl: liveUrl.trim() || null,
        },
      );
      setIsLive(data.isLive);
      setNextLiveAt(data.nextLiveAt ? dayjs(data.nextLiveAt) : null);
      setLiveUrl(data.liveUrl ?? "");
      setSaved(true);
    } catch {
      setError("Could not save the live settings.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card sx={{ p: isMobile ? 2 : 4, maxWidth: 720 }}>
      <Typography variant="h5" fontWeight={700}>
        Live Stream
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
        Control the live indicator shown in the mini-app.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            mt: 4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              p: 2,
              border: "1px solid",
              borderColor: isLive ? "success.light" : "divider",
              borderRadius: 2,
              backgroundColor: isLive
                ? alpha(theme.palette.success.main, 0.08)
                : "background.paper",
            }}
          >
            <Box>
              <Typography fontWeight={700}>Live status</Typography>
              <Typography variant="body2" color="text.secondary">
                {isLive
                  ? "The mini-app indicator is active and animated."
                  : "The mini-app indicator is inactive."}
              </Typography>
            </Box>
            <FormControlLabel
              label={isLive ? "Active" : "Inactive"}
              labelPlacement="start"
              sx={{ m: 0, fontWeight: 700 }}
              control={
                <Switch
                  checked={isLive}
                  onChange={(event) => setIsLive(event.target.checked)}
                  color="success"
                />
              }
            />
          </Box>

          <DateTimePicker
            label="Next live date"
            value={nextLiveAt}
            onChange={setNextLiveAt}
            ampm={false}
            slotProps={{
              textField: {
                fullWidth: true,
                helperText:
                  "Displayed in the mini-app when the live status is inactive.",
              },
              actionBar: { actions: ["clear", "accept"] },
            }}
          />

          <TextField
            label="Live link (optional)"
            value={liveUrl}
            onChange={(event) => setLiveUrl(event.target.value)}
            placeholder="https://example.com/live"
            error={!isValidLiveUrl(liveUrl)}
            helperText={
              isValidLiveUrl(liveUrl)
                ? "When set, clicking the mini-app live box opens this page directly."
                : "Enter a complete http:// or https:// URL."
            }
            fullWidth
          />

          <Button
            variant="contained"
            size="large"
            onClick={handleSave}
            disabled={
              isSaving ||
              (nextLiveAt !== null && !nextLiveAt.isValid()) ||
              !isValidLiveUrl(liveUrl)
            }
            sx={{ alignSelf: isMobile ? "stretch" : "flex-start", minWidth: 140 }}
          >
            {isSaving ? "Saving..." : "Save changes"}
          </Button>
        </Box>
      )}

      <Snackbar
        open={saved}
        autoHideDuration={3500}
        onClose={() => setSaved(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSaved(false)}>
          Live settings saved.
        </Alert>
      </Snackbar>
    </Card>
  );
}
